import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Layout } from '~/components/UI';
import { ModuleHeader } from '~/components/ModuleHeader';
import { Container, Header, HeaderLeftContentWrapper, Main } from './styles';
import { normalizeToFilter } from '~/utils/formatters';
import { Button, Drawer, Dropdown, Icon, Input, Menu, message, Radio, Tooltip, Badge, Popover } from 'antd';
import TableList from './components/table';
import DrawerSolicitation from './components/DrawerSolicitation';
import Search from '~/_shared/components/Search';
import FilterPillsBar from '~/_shared/components/FilterPillsBar';
import RestoredFiltersNotice from '~/_shared/components/RestoredFiltersNotice';
import usePersistedFilters from '~/_shared/hooks/usePersistedFilters';
import {
    SOLICITATIONS_FILTERS_KEY,
    getDefaultSolicitationsFilters,
    countActiveSolicitationsFilters,
} from './filters';

export const Page = () => {

    // Filters kept across refreshes for a few minutes (per tenant/user/page).
    // The list comes from AppContext and is filtered here, so nothing reloads.
    var persisted = usePersistedFilters(SOLICITATIONS_FILTERS_KEY, getDefaultSolicitationsFilters);
    var statusFilter = persisted.filters.statusFilter;
    // Raw text in the field (keeps what the user typed); normalized for matching.
    var searchText = persisted.filters.filter;
    var filter = normalizeToFilter(searchText || '');
    var setFilter = function(value) { persisted.setFilters({ filter: value }); };
    var setStatusFilter = function(value) { persisted.setFilters({ statusFilter: value }); };
    var handleClearAll = function() { persisted.resetFilters(); };
    var activeFilterCount = countActiveSolicitationsFilters(persisted.filters, persisted.defaults);
    const [selectedRow, setSelectedRow] = useState(null);
    const [linkDrawerVisible, setLinkDrawerVisible] = useState(false);
    const [customerSearch, setCustomerSearch] = useState('');

    const data = useSelector(function(state) { return state.solicitations.data; });
    const customers = useSelector(function(state) { return state.customers.list || []; });
    const tenantId = useSelector(function(state) { return state.user && state.user.tenant && state.user.tenant.id; });

    var activeCustomers = useMemo(function() {
        var list = customers.filter(function(c) {
            return c.enabled && !c.inactived_at && c.is_customer;
        });
        // Sort: enabled + token first, then enabled without token, then disabled NF-e
        list.sort(function(a, b) {
            var aReady = a.allow_nf && a.public_nfe_token ? 0 : a.allow_nf ? 1 : 2;
            var bReady = b.allow_nf && b.public_nfe_token ? 0 : b.allow_nf ? 1 : 2;
            if (aReady !== bReady) return aReady - bReady;
            return (a.name || '').localeCompare(b.name || '');
        });
        return list;
    }, [customers]);

    var filteredCustomers = useMemo(function() {
        if (!customerSearch) return activeCustomers;
        var term = customerSearch.toLowerCase();
        return activeCustomers.filter(function(c) {
            var searchable = ((c.cod || '') + ' ' + (c.name || '')).toLowerCase();
            return searchable.indexOf(term) !== -1;
        });
    }, [activeCustomers, customerSearch]);

    var pendingCount = data.filter(function(r) { return r.status === 0 && !r.deleted_at; }).length;
    var completedCount = data.filter(function(r) { return r.status === 1 && !r.deleted_at; }).length;
    var archivedCount = data.filter(function(r) { return !!r.deleted_at; }).length;

    var filteredData = useMemo(function() {
        if (statusFilter === 'pending') return data.filter(function(r) { return r.status === 0 && !r.deleted_at; });
        if (statusFilter === 'completed') return data.filter(function(r) { return r.status === 1 && !r.deleted_at; });
        if (statusFilter === 'archived') return data.filter(function(r) { return !!r.deleted_at; });
        return data;
    }, [data, statusFilter]);

    var handleCopyLink = function(customer) {
        var domain = process.env.REACT_APP_CUSTOMER_DOMAIN || 'localhost:3100';
        var link = domain + '/solicitar-nfe/' + tenantId + '/' + customer.public_nfe_token;
        if (link.indexOf('http') !== 0) {
            link = 'https://' + link;
        }
        navigator.clipboard.writeText(link);
        message.success('Link copiado: ' + customer.name);
        setLinkDrawerVisible(false);
        setCustomerSearch('');
    };

  return (
    <div>
        <Layout>
            <Container>
                    <ModuleHeader
                        breadcrumbs={['Solicitações de NFs']}
                        title="Solicitações de NFs"
                        search={
                            <Search
                                value={searchText}
                                onChange={function(e) { setFilter(e.target.value); }}
                                placeholder="Buscar solicitações"
                            />
                        }
                        center={
                            <>
                                <Popover
                                    trigger="click"
                                    placement="bottomRight"
                                    content={
                                        <div style={{ width: 320 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                                                <span style={{ fontSize: 15, fontWeight: 600, color: '#262626' }}>Filtros</span>
                                                {!persisted.isDefault && (
                                                    <a style={{ fontSize: 12, color: '#f5222d' }} onClick={handleClearAll}>
                                                        Limpar tudo
                                                    </a>
                                                )}
                                            </div>
                                            <div>
                                                <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</div>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                    {[
                                                        { value: 'pending', label: 'Pendentes', color: '#fa8c16', desc: 'Solicitações aguardando emissão.', count: pendingCount },
                                                        { value: 'completed', label: 'Concluídos', color: '#52c41a', desc: 'Solicitações já emitidas.', count: completedCount },
                                                        { value: 'archived', label: 'Arquivados', color: '#8c8c8c', desc: 'Solicitações arquivadas.', count: archivedCount },
                                                    ].map(function(opt) {
                                                        var isActive = statusFilter === opt.value;
                                                        return (
                                                            <div
                                                                key={opt.value}
                                                                onClick={function() { setStatusFilter(opt.value); }}
                                                                style={{
                                                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                                    padding: '8px 12px', borderRadius: 6, cursor: 'pointer',
                                                                    border: '1.5px solid ' + (isActive ? opt.color : '#e8e8e8'),
                                                                    background: isActive ? opt.color + '0a' : '#fafafa',
                                                                    transition: 'all 0.2s',
                                                                }}
                                                            >
                                                                <div>
                                                                    <div style={{ fontSize: 13, fontWeight: isActive ? 600 : 400, color: isActive ? '#262626' : '#8c8c8c' }}>
                                                                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: opt.color, display: 'inline-block', marginRight: 8 }} />
                                                                        {opt.label}
                                                                    </div>
                                                                    <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2, marginLeft: 16 }}>{opt.desc}</div>
                                                                </div>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                    <span style={{ fontSize: 13, fontWeight: 600, color: isActive ? opt.color : '#bfbfbf' }}>{opt.count}</span>
                                                                    {isActive && <Icon type="check" style={{ color: opt.color, fontSize: 14 }} />}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    }
                                >
                                    <Badge count={activeFilterCount} offset={[-4, 0]} style={{ backgroundColor: '#722ed1' }}>
                                        <Button icon="bars" style={{ borderRadius: 8 }}>Filtros</Button>
                                    </Badge>
                                </Popover>
                                <Dropdown
                                    trigger={['click']}
                                    overlay={
                                        <Menu>
                                            <Menu.Item key="link" onClick={function() { setLinkDrawerVisible(true); }}>
                                                <Icon type="link" /> Link Público
                                            </Menu.Item>
                                        </Menu>
                                    }
                                >
                                    <Button icon="plus" shape="circle" />
                                </Dropdown>
                            </>
                        }
                        advanced={
                            (function() {
                                var STATUS_MAP = { pending: { label: 'Pendentes', color: '#fa8c16' }, completed: { label: 'Concluídos', color: '#52c41a' }, archived: { label: 'Arquivados', color: '#8c8c8c' } };
                                var current = STATUS_MAP[statusFilter] || STATUS_MAP.pending;
                                var pills = [
                                    { key: 'status', label: current.label, color: current.color, onRemove: statusFilter !== 'pending' ? function() { setStatusFilter('pending'); } : undefined },
                                ];
                                if (searchText) {
                                    pills.push({ key: 'search', label: 'Busca: ' + searchText, color: '#722ed1', icon: 'search', onRemove: function() { setFilter(''); } });
                                }
                                return React.createElement(FilterPillsBar, {
                                    pills: pills,
                                    onClearAll: !persisted.isDefault ? handleClearAll : undefined,
                                });
                            })()
                        }
                    />
                    <RestoredFiltersNotice
                        visible={persisted.wasRestored}
                        summary={[
                            statusFilter !== persisted.defaults.statusFilter
                                ? ({ completed: 'Concluídos', archived: 'Arquivados', pending: 'Pendentes' }[statusFilter] || statusFilter)
                                : '',
                            searchText ? 'Busca: ' + searchText : '',
                        ].filter(Boolean).join(' · ')}
                        onReset={handleClearAll}
                        onDismiss={persisted.dismissRestored}
                    />
                <Main>
                    <DrawerSolicitation
                        data={selectedRow}
                        onClose={() => setSelectedRow(null)}
                    />
                    <TableList
                        data={filteredData}
                        filter={filter}
                        setSelectedRow={setSelectedRow}
                    />
                </Main>
            </Container>
        </Layout>

        <Drawer
            title={null}
            placement="right"
            width={420}
            visible={linkDrawerVisible}
            onClose={function() { setLinkDrawerVisible(false); setCustomerSearch(''); }}
            bodyStyle={{ padding: 0 }}
        >
            <div style={{ padding: '24px 24px 0' }}>
                <div style={{ marginBottom: 4 }}>
                    <Icon type="link" style={{ fontSize: 20, color: '#1890ff', marginRight: 8 }} />
                    <span style={{ fontSize: 18, fontWeight: 600, color: 'rgba(0,0,0,0.85)' }}>Link Público de NF-e</span>
                </div>
                <p style={{ fontSize: 13, color: 'rgba(0,0,0,0.4)', margin: '4px 0 20px' }}>
                    Selecione um cliente para copiar o link de solicitação de NF-e.
                </p>
                <Input
                    placeholder="Buscar por nome ou código..."
                    prefix={<Icon type="search" style={{ color: 'rgba(0,0,0,0.25)' }} />}
                    value={customerSearch}
                    onChange={function(e) { setCustomerSearch(e.target.value); }}
                    allowClear
                    size="large"
                    style={{ borderRadius: 8 }}
                    autoFocus
                />
                {activeCustomers.length > 0 && (
                    <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.35)', marginTop: 8, marginBottom: 4 }}>
                        {filteredCustomers.length} {filteredCustomers.length === 1 ? 'cliente' : 'clientes'}
                    </div>
                )}
            </div>

            <div style={{ padding: '8px 16px', overflowY: 'auto', maxHeight: 'calc(100vh - 220px)' }}>
                {activeCustomers.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '48px 24px', color: 'rgba(0,0,0,0.3)' }}>
                        <Icon type="disconnect" style={{ fontSize: 40, marginBottom: 16, display: 'block' }} />
                        <p style={{ fontSize: 15, fontWeight: 500, color: 'rgba(0,0,0,0.5)', margin: '0 0 4px' }}>Nenhum cliente com link ativo</p>
                        <p style={{ fontSize: 13, margin: 0 }}>Ative o serviço de NFs no cadastro do cliente.</p>
                    </div>
                ) : filteredCustomers.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '32px 24px', color: 'rgba(0,0,0,0.3)' }}>
                        <p style={{ fontSize: 14, margin: 0 }}>Nenhum cliente encontrado</p>
                    </div>
                ) : (
                    filteredCustomers.map(function(customer) {
                        var hasNf = !!customer.allow_nf;
                        var hasToken = !!customer.public_nfe_token;
                        var isReady = hasNf && hasToken;
                        var tooltipMsg = !hasNf
                            ? 'O serviço de NF-e não está ativado para este cliente. Acesse o cadastro, aba Serviços, e ative a opção NF-e.'
                            : !hasToken
                            ? 'Token ainda não gerado. Salve o cadastro do cliente na aba Serviços para gerar o link automaticamente.'
                            : '';
                        var row = (
                            <div
                                key={customer.id}
                                onClick={isReady ? function() { handleCopyLink(customer); } : undefined}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                    padding: '12px 12px',
                                    borderRadius: 8,
                                    cursor: isReady ? 'pointer' : 'not-allowed',
                                    transition: 'all 0.15s',
                                    marginBottom: 2,
                                    opacity: isReady ? 1 : 0.4,
                                }}
                                onMouseEnter={isReady ? function(e) { e.currentTarget.style.background = '#e6f7ff'; } : undefined}
                                onMouseLeave={isReady ? function(e) { e.currentTarget.style.background = 'transparent'; } : undefined}
                            >
                                <div style={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 8,
                                    background: isReady ? '#f0f5ff' : '#f5f5f5',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}>
                                    <Icon type={isReady ? 'copy' : 'lock'} style={{ fontSize: 16, color: isReady ? '#1890ff' : '#bfbfbf' }} />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 14, fontWeight: 500, color: isReady ? 'rgba(0,0,0,0.85)' : 'rgba(0,0,0,0.35)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {customer.name}
                                    </div>
                                    <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.4)' }}>
                                        {customer.cod}{!hasNf ? ' · NF-e desativado' : ''}
                                    </div>
                                </div>
                                {isReady && <Icon type="right" style={{ fontSize: 12, color: 'rgba(0,0,0,0.15)', flexShrink: 0 }} />}
                            </div>
                        );
                        if (!isReady) {
                            return (
                                <Tooltip key={customer.id} title={tooltipMsg} placement="left">
                                    {row}
                                </Tooltip>
                            );
                        }
                        return row;
                    })
                )}
            </div>
        </Drawer>
    </div>
  );
};

export default Page;
